import { useState } from 'react'
import { Layout } from 'components/Layout'
import { CurrentUserContextProvider } from 'context/useCurrentUser'
import { notifications, NotificationProvider } from 'context'
import {
  AddStudentsModalProvider,
  AddStudentsModal,
  useAddStudentsModal,
} from 'pages/TeacherDashboard/components/AddStudentsModal'
import { useTranslation } from 'react-i18next'
import { IconCheck, IconPlus, IconUsersGroup, IconX } from '@tabler/icons-react'
import type { ManagedStudent } from 'api/arcMosaico/types'
import { ArcPageShell } from 'components/ArcPageShell'
import { Button } from 'components/Button'
import { useArcTranslation } from 'hooks'
import { StudentFilters } from './components/StudentFilters'
import { StudentGrid } from './components/StudentGrid'
import { EditStudentModal } from './components/EditStudentModal'
import { ResetPasswordModal } from './components/ResetPasswordModal'
import { ManageGroupsModal } from './components/ManageGroupsModal'
import { useManageStudents } from './hooks/useManageStudents'

function ManageStudentsContent() {
  const {
    students,
    groups,
    grades,
    loading,
    totalPages,
    filters,
    setSearch,
    setGroupFilter,
    setPage,
    editStudent,
    resetPassword,
    addGroup,
    editGroup,
    removeGroup,
    fetchStudents,
    fetchGroups,
  } = useManageStudents()

  return (
    <AddStudentsModalProvider onStudentsAdded={fetchStudents} onGroupCreated={fetchGroups}>
      <ManageStudentsView
        students={students}
        groups={groups}
        grades={grades}
        loading={loading}
        totalPages={totalPages}
        filters={filters}
        setSearch={setSearch}
        setGroupFilter={setGroupFilter}
        setPage={setPage}
        editStudent={editStudent}
        resetPassword={resetPassword}
        addGroup={addGroup}
        editGroup={editGroup}
        removeGroup={removeGroup}
      />
    </AddStudentsModalProvider>
  )
}

function ManageStudentsView({
  students, groups, grades, loading, totalPages, filters,
  setSearch, setGroupFilter, setPage,
  editStudent, resetPassword,
  addGroup, editGroup, removeGroup,
}: {
  students: ManagedStudent[]
  groups: ReturnType<typeof useManageStudents>['groups']
  grades: ReturnType<typeof useManageStudents>['grades']
  loading: boolean
  totalPages: number
  filters: ReturnType<typeof useManageStudents>['filters']
  setSearch: (s: string) => void
  setGroupFilter: (id: string | null) => void
  setPage: (p: number) => void
  editStudent: ReturnType<typeof useManageStudents>['editStudent']
  resetPassword: ReturnType<typeof useManageStudents>['resetPassword']
  addGroup: ReturnType<typeof useManageStudents>['addGroup']
  editGroup: ReturnType<typeof useManageStudents>['editGroup']
  removeGroup: ReturnType<typeof useManageStudents>['removeGroup']
}) {
  const { open: openAddStudentsModal } = useAddStudentsModal()
  const { t } = useTranslation()
  // Header strings keep resolving arc_ variants, exactly as the old PageHeader did
  const { t: tHeader } = useArcTranslation()

  const [editingStudent, setEditingStudent] =
    useState<ManagedStudent | null>(null)
  const [resetPwStudent, setResetPwStudent] =
    useState<ManagedStudent | null>(null)
  const [manageGroupsOpen, setManageGroupsOpen] =
    useState(false)

  const groupOptions = groups.map(
    g => ({ value: g.id, label: g.name }),
  )

  const handleEditSave = async (data: {
    firstName: string; lastName: string; gradeId: string | null
    groupIds: string[]; username: string; email: string
  }) => {
    if (!editingStudent) return
    try {
      await editStudent(editingStudent.id, {
        firstName: data.firstName,
        lastName: data.lastName,
        username: data.username,
        email: data.email,
        gradeId: data.gradeId,
        groupSchoolTermIds: data.groupIds,
      })
      setEditingStudent(null)
      notifications.show({
        message: t('manage_students.toast_edit_success'),
        color: 'green',
        icon: <IconCheck size={18} />,
      })
    } catch (err) {
      notifications.show({
        message: err instanceof Error ? err.message : t('manage_students.toast_error'),
        color: 'red',
        icon: <IconX size={18} />,
      })
    }
  }

  const handleResetPassword = async (password: string) => {
    if (!resetPwStudent) return
    try {
      await resetPassword(resetPwStudent.id, password)
      setResetPwStudent(null)
      notifications.show({
        message: t('manage_students.toast_reset_success'),
        color: 'green',
        icon: <IconCheck size={18} />,
      })
    } catch (err) {
      notifications.show({
        message: err instanceof Error ? err.message : t('manage_students.toast_error'),
        color: 'red',
        icon: <IconX size={18} />,
      })
    }
  }

  return (
    <Layout>
      {/* No back link: Students is a sidebar destination in the Arc menu
          (back rule), even though it is also reachable from School Settings */}
      <ArcPageShell
        title={tHeader('manage_students.title')}
        subtitle={tHeader('manage_students.subtitle')}
        actions={
          <>
            <Button
              variant='filled'
              size='md'
              leftIcon={<IconUsersGroup size={18} />}
              onClick={() => setManageGroupsOpen(true)}
            >
              {tHeader('manage_students.manage_groups')}
            </Button>
            <Button
              variant='filled'
              size='md'
              leftIcon={<IconPlus size={18} />}
              onClick={() => openAddStudentsModal()}
            >
              {tHeader('manage_students.add_students')}
            </Button>
          </>
        }
      >
        <StudentFilters
          search={filters.search}
          onSearchChange={setSearch}
          selectedGroupId={filters.groupId}
          onGroupChange={setGroupFilter}
          groupOptions={groupOptions}
        />

        <StudentGrid
          students={students}
          loading={loading}
          page={filters.page}
          totalPages={totalPages}
          onPageChange={setPage}
          onEdit={setEditingStudent}
          onResetPassword={setResetPwStudent}
        />

        <AddStudentsModal />

        <EditStudentModal
          student={editingStudent}
          groups={groups}
          grades={grades}
          onClose={() => setEditingStudent(null)}
          onSave={handleEditSave}
        />

        <ResetPasswordModal
          student={resetPwStudent}
          onClose={() => setResetPwStudent(null)}
          onSave={handleResetPassword}
        />

        <ManageGroupsModal
          opened={manageGroupsOpen}
          onClose={() => setManageGroupsOpen(false)}
          groups={groups}
          onCreateGroup={addGroup}
          onEditGroup={editGroup}
          onDeleteGroup={removeGroup}
        />
      </ArcPageShell>
    </Layout>
  )
}

export function ManageStudentsPage() {
  return (
    <CurrentUserContextProvider>
      <NotificationProvider>
        <ManageStudentsContent />
      </NotificationProvider>
    </CurrentUserContextProvider>
  )
}
